import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { SignOut, Gauge, ShieldStar, Compass } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { TYPES, TYPE_META, SECTIONS, SECTION_META, ROLE_META, HOME_ICON } from "@/lib/meta";

function NavItem({ to, icon: Icon, label, testid, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 font-mono text-[13px] tracking-wide border-l-2 transition-colors ${
          isActive
            ? "border-primary bg-secondary text-foreground"
            : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/50"
        }`
      }
    >
      <Icon size={18} weight="regular" />
      {label}
    </NavLink>
  );
}

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = ROLE_META[user?.role] || ROLE_META.lead;

  const doLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-border bg-card/40 sticky top-0 h-screen">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-2">
            <Compass size={22} className="text-primary" weight="fill" />
            <span className="font-head text-lg font-medium tracking-tight">Nettpilot</span>
          </div>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Portal</span>
        </div>

        <nav className="flex-1 py-4">
          <p className="px-4 pb-2 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground/60">Overview</p>
          <NavItem to="/portal" end icon={HOME_ICON} label="Dashboard" testid="nav-dashboard" />
          <p className="px-4 pt-5 pb-2 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground/60">Services</p>
          {SECTIONS.map((s) => (
            <NavItem key={s} to={`/portal/section/${s}`} icon={SECTION_META[s].icon} label={SECTION_META[s].label} testid={`nav-${s}`} />
          ))}
          <p className="px-4 pt-5 pb-2 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground/60">Library</p>
          {TYPES.map((t) => (
            <NavItem key={t} to={`/portal/${t}`} icon={TYPE_META[t].icon} label={TYPE_META[t].label} testid={`nav-${t}`} />
          ))}
          {["admin", "super_admin"].includes(user?.role) && (
            <>
              <p className="px-4 pt-5 pb-2 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground/60">Manage</p>
              <NavItem to="/admin" icon={ShieldStar} label="Admin Panel" testid="nav-admin" />
            </>
          )}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="mb-3">
            <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate font-mono">{user?.company}</p>
            <span className={`inline-block mt-2 px-2 py-0.5 border text-[10px] font-mono tracking-widest uppercase ${role.color}`}>
              {role.label}
            </span>
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-button"
            className="flex items-center gap-2 w-full px-3 py-2 font-mono text-xs tracking-wide text-muted-foreground hover:text-primary hover:bg-secondary transition-colors"
          >
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="md:hidden sticky top-0 z-40 flex items-center justify-between px-4 h-14 border-b border-border backdrop-blur-xl bg-background/70">
          <div className="flex items-center gap-2">
            <Compass size={20} className="text-primary" weight="fill" />
            <span className="font-head font-medium">Nettpilot Portal</span>
          </div>
          <button onClick={doLogout} data-testid="logout-button-mobile" className="text-muted-foreground">
            <SignOut size={18} />
          </button>
        </header>
        {/* Mobile nav */}
        <div className="md:hidden flex overflow-x-auto border-b border-border">
          <NavLink to="/portal" end className={({isActive})=>`px-4 py-3 font-mono text-xs whitespace-nowrap ${isActive?"text-primary border-b-2 border-primary":"text-muted-foreground"}`}>Dashboard</NavLink>
          {SECTIONS.map((s)=>(
            <NavLink key={s} to={`/portal/section/${s}`} className={({isActive})=>`px-4 py-3 font-mono text-xs whitespace-nowrap ${isActive?"text-primary border-b-2 border-primary":"text-muted-foreground"}`}>{SECTION_META[s].label}</NavLink>
          ))}
          {TYPES.map((t)=>(
            <NavLink key={t} to={`/portal/${t}`} className={({isActive})=>`px-4 py-3 font-mono text-xs whitespace-nowrap ${isActive?"text-primary border-b-2 border-primary":"text-muted-foreground"}`}>{TYPE_META[t].label}</NavLink>
          ))}
          {["admin", "super_admin"].includes(user?.role) && <NavLink to="/admin" className={({isActive})=>`px-4 py-3 font-mono text-xs whitespace-nowrap ${isActive?"text-primary border-b-2 border-primary":"text-muted-foreground"}`}>Admin</NavLink>}
        </div>

        <motion.main
          key={window.location.pathname}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="flex-1 p-6 sm:p-10 max-w-6xl w-full"
        >
          <Outlet />
        </motion.main>
      </div>
    </div>
  );
}
